import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  createBlobPreviewUrl,
  revokeBlobPreviewUrl,
} from '../../Events/ManageEvent/coverImageUtils';
import { validatePartnerMediaFile } from './companyInfoDefaults';

export const usePartnerMediaOptimization = ({
  file,
  preview,
  onChange,
  regenerateMutation,
  noun,
  toastId,
}) => {
  const abortControllerRef = useRef(null);
  const previousRef = useRef(null);
  const localPreviewRef = useRef('');

  const [optimizing, setOptimizing] = useState(false);
  const [optimizationError, setOptimizationError] = useState('');
  const [pendingUpload, setPendingUpload] = useState(false);

  const clearLocalPreview = useCallback(() => {
    if (localPreviewRef.current) {
      revokeBlobPreviewUrl(localPreviewRef.current);
      localPreviewRef.current = '';
    }
  }, []);

  const cancelOptimization = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setOptimizing(false);
    setOptimizationError('');
    setPendingUpload(false);

    clearLocalPreview();

    if (previousRef.current) {
      onChange(previousRef.current);
    } else {
      onChange({ file: null, preview: '', removed: true });
    }

    previousRef.current = null;
  }, [clearLocalPreview, onChange]);

  const handleSelected = useCallback(
    async (nextFile) => {
      const validation = validatePartnerMediaFile(nextFile, noun);
      if (!validation.valid) {
        toast.error(validation.message || `Invalid ${noun.toLowerCase()}.`);
        return;
      }

      previousRef.current = {
        file,
        preview,
        removed: false,
      };

      clearLocalPreview();

      const localPreview = createBlobPreviewUrl(nextFile);
      localPreviewRef.current = localPreview;

      onChange({
        file: null,
        preview: localPreview,
        removed: false,
      });

      setOptimizing(true);
      setOptimizationError('');
      setPendingUpload(true);

      const controller = new AbortController();
      abortControllerRef.current = controller;

      toast.loading(`Optimizing ${noun.toLowerCase()}… This may take up to 2 minutes.`, {
        id: toastId,
      });

      try {
        const optimizedFile = await regenerateMutation.mutateAsync({
          file: nextFile,
          signal: controller.signal,
        });

        const optimizedPreview = createBlobPreviewUrl(optimizedFile);
        clearLocalPreview();

        onChange({
          file: optimizedFile,
          preview: optimizedPreview,
          removed: false,
        });

        setOptimizing(false);
        setPendingUpload(false);
        previousRef.current = null;
        toast.success(`${noun} optimized successfully.`, { id: toastId });
      } catch (error) {
        if (axios.isCancel(error)) {
          toast.dismiss(toastId);
          return;
        }

        setOptimizing(false);
        setOptimizationError(error?.message || `Failed to optimize ${noun.toLowerCase()}.`);
        toast.error(error?.message || `Failed to optimize ${noun.toLowerCase()}.`, {
          id: toastId,
        });
      } finally {
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      }
    },
    [clearLocalPreview, file, noun, onChange, preview, regenerateMutation, toastId],
  );

  useEffect(
    () => () => {
      abortControllerRef.current?.abort();
      clearLocalPreview();
    },
    [clearLocalPreview],
  );

  return {
    optimizing,
    optimizationError,
    pendingUpload,
    handleSelected,
    cancelOptimization,
  };
};
